import DeleteCell from '@nile/client_management/src/Pages/Invoice/DeleteCell';
import EditCell from '@nile/client_management/src/Pages/Invoice/EditCell';
import { InvoiceListCell } from '@nile/client_management/src/Pages/Invoice/InvoiceListCell';
import { DeletePaymentModal } from '@nile/client_management/src/Pages/Protected/ClientNavigation/DeletePaymentModal';
import { EditPaymentModal } from '@nile/client_management/src/Pages/Protected/ClientNavigation/EditPaymentModal';
import { InvoiceTransactionsListModal } from '@nile/client_management/src/Pages/Protected/ClientNavigation/InvoiceTransactionsListModal';
import {
  generateOptions,
  getDefaultValue,
  getDynamicQuery
} from '@nile/client_management/src/Pebbles/InvoiceContainer';
import {
  ALL_TABLES,
  getDateOperators,
  getTranslatedText,
  SkeletonTableLoading,
  SuperList,
  useSuperListHook,
  useIsAuthorisedHook,
  NoData,
  EventEmitter,
  useActivityTracker
} from '@nile/components';
import { GET_PAYMENT_LIST } from '@nile/graphql/src/Query/client_management/partialPayments';
import envConfig from '@nile/home/src/Config/env';
import DateTimeCell from '@nile/mangifera/src/Components/Ticket/DateTimeCell';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { AccountClient } from '@nile/graphql';
import CurrencyCell from './CurrencyCell';

function DateTimeCellWrapper(props) {
  const { cell } = props;
  return (
    <DateTimeCell
      {...props}
      cell={{ ...cell, column: { ...cell.column, dataType: 'date' } }}
    />
  );
}

function DeleteCellWrapper(props) {
  const { cell } = props;
  const meta = cell?.row?.original;
  if (meta?.paymentSource !== 'SUPEROPS') return null;
  return <DeleteCell {...props} />;
}

function EditCellWrapper(props) {
  const { cell } = props;
  const meta = cell?.row?.original;
  if (meta?.paymentSource !== 'SUPEROPS') return null;
  return <EditCell {...props} />;
}

const defaultColumnCells = {
  InvoiceListCell,
  Delete: DeleteCellWrapper,
  Edit: EditCellWrapper,
  DateTimeCell: DateTimeCellWrapper,
  CurrencyCell
};

export function PaymentContainer({
  clientId,
  hideActions,
  conditions,
  hideColumns = []
}) {
  const intl = useIntl();
  const tableName = ALL_TABLES.paymentList;

  const {
    tableCondition,
    updateTableFilterCondition,
    updateAdvanceFilterCondition,
    advanceFilterCondition
  } = useSuperListHook({
    tableName,
    viewId: tableName
  });

  const [data, setData] = useState([]);
  const [columnGroup, setColumnGroup] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [activeData, setActiveData] = useState(null);

  const isInvoiceEditAuthorised = useIsAuthorisedHook('invoice.edit');
  const isInvoiceDeleteAuthorised = useIsAuthorisedHook('invoice.delete');

  useEffect(() => {
    if (!conditions) return;
    updateAdvanceFilterCondition({
      joinOperator: 'AND',
      operands: conditions
    });
  }, [conditions]);

  function onAction({ name }) {
    // eslint-disable-next-line default-case
    switch (name) {
      case 'edit':
        useActivityTracker({
          event: 'PAYMENT_EDIT_CLICKED'
        });
        return {
          click: function onPaymentEdit(original) {
            setActiveData({ mode: 'edit', data: original });
          }
        };
      case 'delete':
        useActivityTracker({
          event: 'PAYMENT_DELETE_CLICKED'
        });
        return {
          onChange: function onPaymentDelete({ miscItem: original }) {
            setActiveData({ mode: 'delete', data: original });
          }
        };

      case 'associatedInvoicesCount':
        return {
          click: function onAssociatedInvoicesClick(original) {
            setActiveData({ mode: 'invoiceList', data: original });
          }
        };
    }
  }

  async function fetchViewData(_, pageOptions) {
    setListLoading(false);
    const cols = [
      {
        name: 'paymentDate',
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.paymentDate'
        ),
        type: 'DateTimeCell',
        isFilterable: true,
        filterType: 'CustomTime',
        dataType: 'daterange',
        isSortable: true,
        uiDataType: 'date'
      },
      {
        name: 'displayId',
        label: getTranslatedText(
          intl,
          'invoice.partialPayments.paymentSummary.paymentId'
        ),
        isFilterable: true,
        filterType: 'text',
        dataType: 'string',
        isSortable: true
      },
      {
        name: 'client.name',
        label: getTranslatedText(intl, 'common.client'),
        isFilterable: true,
        autoComplete: false,
        checkDynamicQuery: true,
        dataType: 'string',
        isSortable: true
      },
      {
        name: 'associatedInvoicesCount',
        label: getTranslatedText(intl, 'invoice.partialPayment.header'),
        type: 'InvoiceListCell'
      },
      {
        name: 'amount',
        label: getTranslatedText(intl, 'common.amount'),
        type: 'CurrencyCell',
        isSortable: true
      },
      {
        name: 'paymentMethod.paymentMethodName',
        label: getTranslatedText(intl, 'invoice.partialPayments.paymentMethod'),
        isFilterable: true,
        checkDynamicQuery: true,
        filterType: 'select',
        dataType: 'string'
      },
      {
        name: 'paymentReference',
        label: getTranslatedText(intl, 'invoice.partialPayments.reference'),
        isFilterable: true,
        filterType: 'text',
        dataType: 'string'
      },
      {
        name: 'balanceAmount',
        label: getTranslatedText(intl, 'invoice.partialPayments.balanceAmount'),
        type: 'CurrencyCell',
        isSortable: true
      }
    ].filter((col) => !hideColumns.includes(col.name));

    const columns = cols.map((col) => {
      return {
        Header: col.label,
        accessor: col.name,
        accessorKey: col.name,
        // minWidth: col.name === 'subject' ? 400 : 50,
        // width: col.name === 'invoiceDescription' ? 400 : 150,
        //   size: getColumnWidth(columnWidthObject, col.name),
        type: col.type || 'Text',
        id: col.name,
        disableResizing: false,
        className: 'sticky',
        disableSortBy: !col.isSortable,
        editable: false,
        disableFilters: !col.isFilterable,
        field: col,
        isBold: col.name === 'amount',
        actions: onAction({ name: col.name }),
        helpers: {
          entity: 'Client',
          checkDynamicQuery: () => col.checkDynamicQuery,
          getDynamicQuery,
          getDefaultValue,
          getDateOperators,
          generateOptions,
          updateConditions: updateTableFilterCondition
          // getAsyncOptions: (property) => {
          //   return { client: AccountClient, query: GET_CLIENTS };
          // },
          // fetchValues
        }
      };
    });
    if (!hideActions) {
      columns.push(
        ...[
          isInvoiceEditAuthorised && {
            Header: ' ',
            header: ' ',
            accessorKey: ' ',
            accessor: ' ',
            size: 50,
            id: 'edit',
            type: 'Edit',
            action: true,
            disableSortBy: true,
            disableFilters: true,
            actions: onAction({ name: 'edit' })
          },
          isInvoiceDeleteAuthorised && {
            Header: ' ',
            header: ' ',
            accessorKey: ' ',
            accessor: ' ',
            size: 50,
            id: 'delete',
            type: 'Delete',
            action: true,
            disableSortBy: true,
            disableFilters: true,
            actions: onAction({ name: 'delete' })
          }
        ].filter(Boolean)
      );
    }
    setColumnGroup(columns);
  }

  useEffect(() => {
    if (!columnGroup) {
      fetchViewData();
    }
  }, []);

  if (listLoading) {
    return <SkeletonTableLoading />;
  }

  return (
    <>
      {activeData?.mode === 'invoiceList' && (
        <InvoiceTransactionsListModal
          open
          onClose={() => setActiveData(null)}
          paymentId={activeData?.data?.paymentId}
        />
      )}
      {activeData?.mode === 'edit' && (
        <EditPaymentModal
          open
          onClose={() => setActiveData(null)}
          data={activeData?.data}
          onSuccess={() => fetchViewData()}
        />
      )}
      {activeData?.mode === 'delete' && (
        <DeletePaymentModal
          open
          onClose={() => setActiveData(null)}
          data={activeData?.data}
          onSuccess={() => fetchViewData()}
        />
      )}

      <SuperList
        advanceFilterCondition={advanceFilterCondition}
        tableName={tableName}
        viewId={tableName}
        columns={columnGroup}
        data={data}
        setData={setData}
        padded="lg"
        defaultColumnCells={defaultColumnCells}
        isLoading={listLoading}
        noBulkSelect
        fullHeight
        emptyState={{
          nodataImage: `${envConfig.imageRoot}svg/partialPaymentsEmpty.svg`,
          heading: getTranslatedText(intl, 'app.selectionsheet.emptyState'),
          description: ''
        }}
        idKey="paymentId"
        client={AccountClient}
        query={GET_PAYMENT_LIST}
        accessorKey="getPaymentList"
        dataKey="payments"
        tableCondition={tableCondition}
        defaultSort={[{ attribute: 'paymentDate', order: 'DESC' }]}
        secondarySort={[{ attribute: 'createdTime', order: 'DESC' }]}
        defaultCondition={{
          joinOperator: 'AND',
          operands: [
            clientId && {
              attribute: 'client.accountId',
              operator: 'is',
              value: clientId
            }
          ].filter(Boolean)
        }}
      />
    </>
  );
}
